import React, { useState, createContext, useContext, ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// Styled Components
const ToastContainer = styled.div`
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 400px;
  width: 100%;
  
  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
`;

const ToastItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'type', 'isExiting'].includes(prop),
})<{ 
  theme: any; 
  type: ToastType; 
  isExiting: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(8px);
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return props.theme.colors.success + '40';
      case 'error': return props.theme.colors.error + '40';
      case 'warning': return '#f59e0b40';
      case 'info': return props.theme.colors.primary + '40';
      default: return props.theme.colors.border;
    }
  }};
  background: ${props => {
    const baseColor = props.theme.colors.surface;
    switch (props.type) {
      case 'success': return `linear-gradient(135deg, ${props.theme.colors.success}15 0%, ${baseColor} 100%)`;
      case 'error': return `linear-gradient(135deg, ${props.theme.colors.error}15 0%, ${baseColor} 100%)`;
      case 'warning': return `linear-gradient(135deg, #f59e0b15 0%, ${baseColor} 100%)`;
      case 'info': return `linear-gradient(135deg, ${props.theme.colors.primary}15 0%, ${baseColor} 100%)`;
      default: return baseColor;
    }
  }};
  animation: ${props => props.isExiting ? slideOut : slideIn} 0.3s ease-out;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => {
      switch (props.type) {
        case 'success': return props.theme.colors.success;
        case 'error': return props.theme.colors.error;
        case 'warning': return '#f59e0b';
        case 'info': return props.theme.colors.primary;
        default: return props.theme.colors.border;
      }
    }};
  }
`;

const ToastIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'type'].includes(prop),
})<{ theme: any; type: ToastType }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => {
    switch (props.type) {
      case 'success': return props.theme.colors.success + '20';
      case 'error': return props.theme.colors.error + '20';
      case 'warning': return '#f59e0b20';
      case 'info': return props.theme.colors.primary + '20';
      default: return props.theme.colors.border + '20';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return props.theme.colors.success;
      case 'error': return props.theme.colors.error;
      case 'warning': return '#f59e0b';
      case 'info': return props.theme.colors.primary;
      default: return props.theme.colors.text;
    }
  }};
`;

const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.h4.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  margin: 0 0 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`;

const ToastMessage = styled.p.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  margin: 0;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.4;
`;

const CloseButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: ${props => props.theme.colors.border}30;
    color: ${props => props.theme.colors.text};
  }
`;

// Toast Item Component
const ToastItemComponent: React.FC<{
  toast: Toast & { isExiting: boolean };
  onClose: (id: string) => void;
}> = ({ toast, onClose }) => {
  const { theme } = useTheme();

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle size={16} />;
      case 'error':
        return <AlertCircle size={16} />;
      case 'warning':
        return <AlertTriangle size={16} />;
      case 'info':
        return <Info size={16} />;
      default:
        return <Info size={16} />;
    }
  };

  return (
    <ToastItem theme={theme} type={toast.type} isExiting={toast.isExiting}>
      <ToastIcon theme={theme} type={toast.type}>
        {getIcon()}
      </ToastIcon>
      <ToastContent>
        <ToastTitle theme={theme}>{toast.title}</ToastTitle>
        {toast.message && (
          <ToastMessage theme={theme}>{toast.message}</ToastMessage>
        )}
      </ToastContent>
      <CloseButton theme={theme} onClick={() => onClose(toast.id)}>
        <X size={16} />
      </CloseButton>
    </ToastItem>
  );
};

// Toast Provider Component
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<(Toast & { isExiting: boolean })[]>([]);

  const showToast = (toastData: Omit<Toast, 'id'>) => {
    const id = Date.now().toString();
    const newToast = {
      ...toastData,
      id,
      isExiting: false,
      duration: toastData.duration || 5000,
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-hide after duration
    setTimeout(() => {
      hideToast(id);
    }, newToast.duration);
  };

  const hideToast = (id: string) => {
    setToasts(prev =>
      prev.map(toast =>
        toast.id === id ? { ...toast, isExiting: true } : toast
      )
    );

    // Remove from DOM after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <ToastContainer>
        {toasts.map(toast => (
          <ToastItemComponent
            key={toast.id}
            toast={toast}
            onClose={hideToast}
          />
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

// Convenience functions
export const toast = {
  success: (title: string, message?: string, duration?: number) => ({
    type: 'success' as const,
    title,
    message,
    duration,
  }),
  error: (title: string, message?: string, duration?: number) => ({
    type: 'error' as const,
    title,
    message,
    duration,
  }),
  warning: (title: string, message?: string, duration?: number) => ({
    type: 'warning' as const,
    title,
    message,
    duration,
  }),
  info: (title: string, message?: string, duration?: number) => ({
    type: 'info' as const,
    title,
    message,
    duration,
  }),
};
